import { useAtomValue } from "jotai";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import { isGlobalCloudAtom, tenantIDAtom } from "../../atoms/GlobalAtoms";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import DropdownMenuDivider from "../Menus/DropdownMenuDivider";
import useUploadGlobalApp, {
  GlobalAppPayload,
} from "../../api/api-hooks/globalApps/useUploadGlobalApp";
import { AppConfirm } from "../../utils/PopupUtil";
import useRevokeGlobalApp from "../../api/api-hooks/globalApps/useRevokeGlobalApp";
import { newToastEvent } from "../../utils/ToastUtil";
import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import { FileInterface } from "../../types/RecordInterfaces";
import { KeyFeature } from "../../types/GlobalInterfaces";

interface CloudAppItem {
  AppID: string;
  PageID: string;
  Title: string;
  Type: string;
  WorkspaceID: string;
  id: string;
}

interface CloudAppData {
  Name: string;
  Description: string;
  Summary: string;
  Title: string;
  Type: string;
  id: string;
  Icon?: string | null;
  GlobalAppID?: string;
  Items: CloudAppItem[];
  DefaultPage?: string;
  Images: Array<any>;
  CoverImage?: FileInterface;
  KeyFeatures: KeyFeature[];
  MSTags: Array<{ Title: string }>;
  InstallationRequirements: Array<{ Title: string }>;
  ReleaseNotes: Array<{ Title: string }>;
}

interface Props {
  itemData: CloudAppData; // The sidebar item data (Should be a cloud app already.)
  setForceUpdateDrawer: (itemData: CloudAppData) => void;
}

export default function GlobalAppLink({
  itemData,
  setForceUpdateDrawer,
}: Props) {
  const tenantID = useAtomValue(tenantIDAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const isGlobal = useAtomValue(isGlobalCloudAtom);

  const isPublished = !!itemData?.GlobalAppID;

  const { uploadGlobalApp, uploadingApp } = useUploadGlobalApp();
  const { revokeGlobalApp, revokingApp } = useRevokeGlobalApp();
  const { cPermissionProfile } = useCloudPermissions();
  const isAdmin =
    cPermissionProfile?.Name === "Cloud Owner" ||
    cPermissionProfile?.Name === "Cloud Administrator";

  // If itemData/tenantID is missing for whatever reason, hide this option.
  if (!itemData || !tenantID) return null;

  // If cloud isn't global or User isn't Cloud admin, hide this option.
  if (!isGlobal || !isAdmin) return null;

  return (
    <>
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name={isPublished ? "Redeploy Global App" : "Deploy App Globally"}
        icon="mdi mdi-cloud-upload"
        onClick={uploadApp}
        disabled={uploadingApp || revokingApp}
      />
      {isPublished && (
        <DropdownOptionWithIcon
          name="Force Update App"
          icon="mdi mdi-alert-outline"
          onClick={() => setForceUpdateDrawer(itemData)}
          disabled={uploadingApp || revokingApp}
        />
      )}
      {isPublished && (
        <DropdownOptionWithIcon
          name="Revoke Global App"
          icon="mdi mdi-upload-off"
          onClick={revokeApp}
          disabled={uploadingApp || revokingApp}
        />
      )}
    </>
  );

  async function uploadApp() {
    const result = await AppConfirm({
      title: `${isPublished ? "Redploy" : "Deploy"} App Globally?`,
      text: `Are you sure you want to ${
        isPublished ? "redeploy" : "deploy"
      } this app globally? It will be visible and installable on all clouds.`,
      confirmButtonText: isPublished ? "Redeploy" : "Deploy",
      confirmButtonColor: "var(--primary)",
    });

    if (!result.isConfirmed) return;

    const payload: GlobalAppPayload = {
      Name: itemData.Name,
      Description: itemData.Description,
      Summary: itemData.Summary,
      Pages: itemData.Items.map((item) => item.PageID),
      OriginCloudID: tenantID as string, // Validated this at the top level of function.
      CloudAppID: itemData.id,
      GlobalAppID: itemData?.GlobalAppID,
      DefaultPage: itemData?.DefaultPage,
      Icon: itemData.Icon || undefined,
      KeyFeatures: itemData.KeyFeatures,
      MSTags: itemData.MSTags,
      InstallationRequirements: itemData?.InstallationRequirements,
      ReleaseNotes: itemData?.ReleaseNotes,
      CoverImage: itemData?.CoverImage,
      Images: itemData?.Images,
    };

    if (itemData.Icon) payload.Icon = itemData.Icon;

    uploadGlobalApp({ payload: payload });
  }

  async function revokeApp() {
    const result = await AppConfirm({
      title: `${isPublished ? "Redploy" : "Deploy"} App Globally?`,
      text: `Are you sure you want to revoke this global app? It will be deleted entirely, and no longer visible to other clouds. However the folder/cloud app will remain.`,
      confirmButtonText: "Revoke",
    });

    if (!result.isConfirmed) return;

    if (!itemData?.GlobalAppID) {
      return newToastEvent(
        "error",
        "Failed to revoke global app. It's ID wasn't found!"
      );
    }

    revokeGlobalApp({
      appID: itemData.GlobalAppID,
      workspaceID: workspaceID as string,
    });
  }
}
